import { ERightType, type IRechargeProductDTO, type TPaywallSlotProduct } from '@/apis/commerce';
import { getActivityOfferSelectionId } from '@/utils/pay/activity-offer-selection';
import { isDiscountSubscribe } from '@/utils/pay/discount-subscribe';
import { resolvePeriodDays } from '@/utils/pay/period-days';

import type { IPaywallOfferGoodsData } from '@/components/paywall-checkout/offer-goods';

/**
 * 网格营销标签。整行轮播（slots.top）不参与。
 * popular / 整本 Save vs coins 属于整行，不在本枚举。
 * discount_off 为折扣订阅的 xx% OFF 角标，仅落在折扣订阅卡上，由标签分配控制数量。
 */
export type TPaywallMarketingLabel = 'most_popular' | 'best_value' | 'ultimate_pack' | 'discount_off';

/** 可创单的权益：金币 / 自动续订 / 整本 / 一次性会员；Unspecified 不可下单 */
export function isCreatableRightType(rightType?: number): boolean {
    return (
        rightType === ERightType.Coin ||
        rightType === ERightType.Membership ||
        rightType === ERightType.FullBook ||
        rightType === ERightType.OneTimeMembership
    );
}

/** 网格会员卡：自动续订或一次性会员 */
export function isGridMembershipProduct(product: TPaywallSlotProduct): boolean {
    return product.right_type === ERightType.Membership || product.right_type === ERightType.OneTimeMembership;
}

/** top 段：无匹配卡则跳过；RT=1 暂无轮播卡；RT=3 不因无 period 跳过 */
export function isCarouselTopVisible(product: TPaywallSlotProduct): boolean {
    const { right_type: rightType } = product;
    if (rightType === ERightType.FullBook) {
        return true;
    }
    if (rightType === ERightType.Membership || rightType === ERightType.OneTimeMembership) {
        return resolvePeriodDays(product.period, product.period_count) != null;
    }
    return false;
}

/** 网格可见：RT=1 金币卡；RT=3 整本（仅竖排单列有卡面，平铺网格由 usePaywallSlotsView 先行剔除）；RT=2 / RT=4 会员卡且 period 可映射 */
export function isGridBottomVisible(product: TPaywallSlotProduct): boolean {
    if (product.right_type === ERightType.Coin || product.right_type === ERightType.FullBook) {
        return true;
    }
    if (isGridMembershipProduct(product)) {
        return resolvePeriodDays(product.period, product.period_count) != null;
    }
    return false;
}

export function filterVisibleTop(products: TPaywallSlotProduct[] | undefined): TPaywallSlotProduct[] {
    return (products ?? []).filter(isCarouselTopVisible);
}

export function filterVisibleBottom(products: TPaywallSlotProduct[] | undefined): TPaywallSlotProduct[] {
    return (products ?? []).filter(isGridBottomVisible);
}

/**
 * 活动与轮播同 product_id 时不重复展示（仅约束活动 vs top）。
 */
export function excludeActivityProductFromTop(
    visibleTop: TPaywallSlotProduct[],
    activityProductId?: string
): TPaywallSlotProduct[] {
    if (!activityProductId) {
        return visibleTop;
    }
    return visibleTop.filter(product => product.product_id !== activityProductId);
}

/**
 * slots.top 本身不会出金币卡；活动插入 visibleTop 时映射为 RT=1。
 * 仅对 visibleTop 项使用，不要拿去判断 bottom。
 */
export function isInjectedActivityTopProduct(product: TPaywallSlotProduct): boolean {
    return product.right_type === ERightType.Coin;
}

/** 轮播实际滑动的 slot top（不含插入的活动卡） */
export function getCarouselTopProducts(visibleTop: TPaywallSlotProduct[] | undefined): TPaywallSlotProduct[] {
    return (visibleTop ?? []).filter(product => !isInjectedActivityTopProduct(product));
}

/** 活动卡选中走虚拟 key，避免和 bottom 同 product_id 一起亮 */
export function getTopItemSelectionId(product: TPaywallSlotProduct): string {
    if (isInjectedActivityTopProduct(product)) {
        return getActivityOfferSelectionId(product.product_id);
    }
    return product.product_id;
}

/** 过滤 slots.top 后把活动插到第一位，供默认选中走统一的 top > bottom */
export function buildVisibleTop(
    slotTop: TPaywallSlotProduct[] | undefined,
    convertedActivity: TPaywallSlotProduct | null
): TPaywallSlotProduct[] {
    const filtered = excludeActivityProductFromTop(filterVisibleTop(slotTop), convertedActivity?.product_id);
    if (!convertedActivity) {
        return filtered;
    }
    return [convertedActivity, ...filtered];
}

/** 有可见商品卡才展示卡片区：bottom 网格非空，或 top 有 slot 商品（仅活动卡不构成） */
export function shouldShowPaywallProductCards(
    visibleBottom: TPaywallSlotProduct[],
    visibleTop?: TPaywallSlotProduct[]
): boolean {
    return visibleBottom.length > 0 || getCarouselTopProducts(visibleTop).length > 0;
}

/**
 * 是否含单充加币（买赠充值）。
 * 无则不展示解锁消耗、余额、Unlock Now 与分隔虚线。
 */
export function hasPaywallRechargeProduct(products: TPaywallSlotProduct[] | undefined): boolean {
    return (products ?? []).some(product => product.right_type === ERightType.Coin);
}

export interface IPaywallSlotsViewInput {
    /** 过滤后的 top，活动已插在第一位 */
    visibleTop: TPaywallSlotProduct[];
    /** 过滤后的 bottom（竖排单列时含整本） */
    visibleBottom: TPaywallSlotProduct[];
    /** 原始活动 offer；选中 id 用虚拟 key 时需要它 */
    activityOffer: IPaywallOfferGoodsData | null;
}

/** 顶部有 slot 商品或活动时走 swiper（仅活动时退化为单卡）；bottom 为空但 top 有 slot 商品（如整本提升）也走 swiper */
export function shouldShowPaywallCarousel(input: IPaywallSlotsViewInput): boolean {
    return shouldShowPaywallProductCards(input.visibleBottom, input.visibleTop) && input.visibleTop.length > 0;
}

/**
 * 默认选中：无任何可见商品不选；有 top（含插入的活动）选 top[0]；
 * 否则 bottom 第一个 is_recommend，没有则 bottom[0]。
 */
export function resolveDefaultSelectionId(input: IPaywallSlotsViewInput): string {
    if (!shouldShowPaywallProductCards(input.visibleBottom, input.visibleTop)) {
        return '';
    }
    const firstTop = input.visibleTop[0];
    if (firstTop) {
        return getTopItemSelectionId(firstTop);
    }
    const recommend = input.visibleBottom.find(product => product.is_recommend);
    return recommend?.product_id ?? input.visibleBottom[0]?.product_id ?? '';
}

/** 本次可见列表的合法选中 id（活动为虚拟 key） */
export function collectValidSelectionIds(input: IPaywallSlotsViewInput): string[] {
    if (!shouldShowPaywallProductCards(input.visibleBottom, input.visibleTop)) {
        return [];
    }
    return [...input.visibleTop.map(getTopItemSelectionId), ...input.visibleBottom.map(product => product.product_id)];
}

/** 已选仍有效则保留，否则回退默认选中 */
export function nextSelectedPackageId(prev: string, input: IPaywallSlotsViewInput): string {
    if (!shouldShowPaywallProductCards(input.visibleBottom, input.visibleTop)) {
        return '';
    }
    const validIds = collectValidSelectionIds(input);
    if (prev && validIds.includes(prev)) {
        return prev;
    }
    return resolveDefaultSelectionId(input);
}

/** 一次性会员（RT=4）无角标；整本不进网格 */
function canReceiveGridMarketingTag(product: TPaywallSlotProduct): boolean {
    return product.right_type === ERightType.Coin || product.right_type === ERightType.Membership;
}

/** 自动续订日均价（分）：折扣订阅按折扣价（与卡面展示口径一致），普通订阅按原价；映射不到 period 则不参与标签评分 */
function resolveSubscribeDailyPriceCents(product: TPaywallSlotProduct): number | undefined {
    if (product.right_type !== ERightType.Membership) {
        return undefined;
    }
    const days = resolvePeriodDays(product.period, product.period_count);
    // isDiscountSubscribe 已保证 discount_price_cents 非空且低于原价
    const priceCents = isDiscountSubscribe(product) ? (product.discount_price_cents as number) : product.price_cents;
    if (days == null || days <= 0 || !(priceCents > 0)) {
        return undefined;
    }
    return priceCents / days;
}

/** 单充每分货币换得的金币+赠币；用于 Ultimate Pack */
function resolveCoinValuePerCent(product: TPaywallSlotProduct): number | undefined {
    if (product.right_type !== ERightType.Coin) {
        return undefined;
    }
    const recharge = product as IRechargeProductDTO;
    const totalCoins = (recharge.coins ?? 0) + (recharge.vouchers ?? 0);
    if (!(product.price_cents > 0) || totalCoins <= 0) {
        return undefined;
    }
    return totalCoins / product.price_cents;
}

interface IUniquePickState {
    best?: TPaywallSlotProduct;
    bestScore?: number;
    tied: boolean;
}

/**
 * 取 score 最值唯一的商品；并列最值或无可评分商品时返回 undefined。
 * 用于「另一个标签」：同类型存在多个最低/最高时跳过该类型。
 */
function pickUniqueScoredProduct(
    products: TPaywallSlotProduct[],
    scoreOf: (product: TPaywallSlotProduct) => number | undefined,
    isBetter: (score: number, bestScore: number) => boolean
): TPaywallSlotProduct | undefined {
    const state = products.reduce<IUniquePickState>(
        (acc, product) => {
            const score = scoreOf(product);
            if (score == null) {
                return acc;
            }
            if (acc.bestScore == null || isBetter(score, acc.bestScore)) {
                return { best: product, bestScore: score, tied: false };
            }
            if (score === acc.bestScore) {
                return { ...acc, tied: true };
            }
            return acc;
        },
        { tied: false }
    );
    return state.tied ? undefined : state.best;
}

/** 普通订阅（非折扣订阅）日均价唯一最低者；并列最低返回 undefined */
function pickBestValueNormalSubscribe(
    products: TPaywallSlotProduct[],
    occupiedIds: Set<string>
): TPaywallSlotProduct | undefined {
    return pickUniqueScoredProduct(
        products.filter(product => !occupiedIds.has(product.product_id) && !isDiscountSubscribe(product)),
        resolveSubscribeDailyPriceCents,
        (a, b) => a < b
    );
}

/** 折扣订阅日均价唯一最低者（排除已占位）；并列最低返回 undefined */
function pickLowestDailyDiscountSubscribe(
    products: TPaywallSlotProduct[],
    occupiedIds: Set<string>
): TPaywallSlotProduct | undefined {
    return pickUniqueScoredProduct(
        products.filter(product => !occupiedIds.has(product.product_id) && isDiscountSubscribe(product)),
        resolveSubscribeDailyPriceCents,
        (a, b) => a < b
    );
}

/** 单充每元金币+赠币唯一最高者；并列最高返回 undefined */
function pickUltimatePackCoin(
    products: TPaywallSlotProduct[],
    occupiedIds: Set<string>
): TPaywallSlotProduct | undefined {
    return pickUniqueScoredProduct(
        products.filter(product => !occupiedIds.has(product.product_id)),
        resolveCoinValuePerCent,
        (a, b) => a > b
    );
}

/**
 * 竖排单列布局判定（不含 top 的空判，调用方自行保证 top 为空）：
 * bottom 1-3 档且全部为订阅会员（RT=2）或整本（RT=3），与 grid-card 单列分发白名单一致；
 * 单列只会有三行商品，含整本购买时不放宽到 4 档。
 * 旧平铺过滤后残留的金币（RT=1）/一次性会员（RT=4）等类型不参与竖排，避免把平铺误判成单列导致卡面兜底 null。
 */
export function isVerticalBottomLayout(products: TPaywallSlotProduct[]): boolean {
    return (
        products.length >= 1 &&
        products.length <= 3 &&
        products.every(
            product => product.right_type === ERightType.Membership || product.right_type === ERightType.FullBook
        )
    );
}

/**
 * 竖排单列的 Popular 归属：打在首个订阅档位（自动续订或一次性会员）上，与选中态解耦。
 * 真实付费墙与预览共用此函数，保证两处标签一致。
 */
export function resolveVerticalPopularProductId(products: TPaywallSlotProduct[]): string | undefined {
    return products.find(isGridMembershipProduct)?.product_id;
}

/**
 * 只打可见 bottom（非整行网格）。整行轮播商品本身不打这几枚标签。
 * 有 slot 轮播 top 时默认选中在轮播，网格不打默认选中标签；仅活动插入 visibleTop 仍打。
 * 默认选中标签 = 网格 is_recommend（无则首个可打标商品）：折扣订阅打 xx% OFF，
 * 普通订阅打 Best Value，单充打 Most Popular；窄卡单充展示自带点赞加赠角标，不占标签位。
 * 剩余标签位按商品类型优先级补齐，直到凑满上限或无可打商品；类型内取最值唯一的商品，
 * 并列最值则跳过该类型，继续取下一类型。上限：有 slot 轮播时最多 1 个，否则最多 2 个：
 * - <=4 宽卡：日均最低的折扣订阅 xx% OFF > 日均最低的普通订阅 Best Value
 *   > 每元金币+赠币最多的单充 Ultimate Pack。
 * - >4 窄卡：只给订阅，日均最低的折扣订阅 xx% OFF > 日均最低的普通订阅 Best Value；
 *   金币单充不打文字标签。
 * 一次性会员不打标。同一商品最多一个标签。
 */
export function resolveBottomMarketingTags(
    visibleBottom: TPaywallSlotProduct[],
    visibleTop?: TPaywallSlotProduct[]
): Map<string, TPaywallMarketingLabel> {
    const labels = new Map<string, TPaywallMarketingLabel>();
    if (visibleBottom.length === 0) {
        return labels;
    }

    const assign = (product: TPaywallSlotProduct | undefined, label: TPaywallMarketingLabel) => {
        if (!product) {
            return;
        }
        labels.set(product.product_id, label);
    };

    const narrow = visibleBottom.length > 4;
    // 有 slot 轮播时默认选中不在网格、网格最多 1 个标签；仅活动插入 visibleTop 仍打默认选中标签
    const hasSlotCarousel = getCarouselTopProducts(visibleTop).length > 0;
    const maxLabels = hasSlotCarousel ? 1 : 2;
    if (!hasSlotCarousel) {
        const recommend =
            visibleBottom.find(product => product.is_recommend && canReceiveGridMarketingTag(product)) ??
            visibleBottom.find(canReceiveGridMarketingTag);
        // 窄卡单充用自带点赞加赠角标表达默认选中，不占营销标签位
        if (recommend && !(narrow && recommend.right_type === ERightType.Coin)) {
            let recommendLabel: TPaywallMarketingLabel = 'most_popular';
            if (isDiscountSubscribe(recommend)) {
                recommendLabel = 'discount_off';
            } else if (recommend.right_type === ERightType.Membership) {
                recommendLabel = 'best_value';
            }
            assign(recommend, recommendLabel);
        }
    }

    // 按类型优先级补齐剩余标签位，直到凑满 maxLabels
    const occupiedIds = new Set(labels.keys());
    const discountSubscribe = pickLowestDailyDiscountSubscribe(visibleBottom, occupiedIds);
    if (discountSubscribe) {
        assign(discountSubscribe, 'discount_off');
        occupiedIds.add(discountSubscribe.product_id);
    }
    if (labels.size < maxLabels) {
        const bestValue = pickBestValueNormalSubscribe(visibleBottom, occupiedIds);
        if (bestValue) {
            assign(bestValue, 'best_value');
            occupiedIds.add(bestValue.product_id);
        }
    }
    // 窄卡的文字标签只给订阅；宽卡无订阅时兜底 Ultimate Pack
    if (labels.size < maxLabels && !narrow) {
        assign(pickUltimatePackCoin(visibleBottom, occupiedIds), 'ultimate_pack');
    }
    return labels;
}

/**
 * 曝光 / 点击 / 创单用的可见商品集合（真实 product_id）。
 * 无任何可见商品时为空数组；否则 visibleTop（已含活动）+ visibleBottom。
 */
export function collectVisibleAllProducts(input: IPaywallSlotsViewInput): TPaywallSlotProduct[] {
    if (!shouldShowPaywallProductCards(input.visibleBottom, input.visibleTop)) {
        return [];
    }
    return [...input.visibleTop, ...input.visibleBottom];
}
